import { useMemo } from 'react'
import classNames from 'classnames'

import Image from '@/components/base/Image'
import Typography from '@/components/base/Typography'

import { WPImage, WPLink } from '@/types'

export type PillarProps = Partial<{
  uuid: number
  title: string
  overline: string
  paragraph: string
  image: WPImage
  logo: WPImage
  link: WPLink
}>

const Pillar = ({
  title,
  overline,
  paragraph,
  image,
  link,
  logo
}: PillarProps) => {
  const needsImage = useMemo(() => image && image.url, [image])

  return (
    <div
      className={classNames(
        'group relative h-full',
        'before:absolute before:left-0 before:top-0 before:z-0 before:h-full before:w-full before:rounded-xxs before:shadow-3xl'
      )}
      data-motion-pillar
    >
      <div
        className={classNames(
          'relative z-10 h-full rounded-xxs bg-white',
          'transition-colors duration-300 ease-out-cubic',
          { 'group-hover:bg-blue-soft': link },
          { 'p-sm': !needsImage }
        )}
      >
        {logo ? (
          <div className="aspect-square relative z-10 mb-xs size-[40px]">
            <Image
              fill
              size="full"
              loading="lazy"
              source={logo}
              alt={logo?.alt}
            />
          </div>
        ) : null}
        {needsImage ? (
          <>
            <div className="aspect-h-[17] aspect-w-[28.8]">
              <Image
                fill
                size="full"
                loading="lazy"
                source={image}
                alt={image?.alt}
                className="rounded-xxs"
              />

              {link ? (
                <a
                  href={link?.url}
                  target={link?.target}
                  className="before:absolute before:left-0 before:top-0 before:h-full before:w-full"
                />
              ) : null}
            </div>
          </>
        ) : (
          <div className="grid gap-y-xs">
            {overline ? (
              <Typography
                variant="caption"
                paragraph
                className={classNames(
                  'text-blue-soft',
                  'transition-colors duration-300 ease-out-cubic',
                  {
                    'group-hover:text-white': link
                  }
                )}
              >
                {overline}
              </Typography>
            ) : null}

            {title ? (
              <Typography
                variant="h6"
                component="h6"
                className={classNames(
                  'text-dark',
                  'transition-colors duration-300 ease-out-cubic',
                  {
                    'group-hover:text-white': link
                  }
                )}
              >
                {title}
              </Typography>
            ) : null}

            {paragraph ? (
              <Typography
                variant="body"
                paragraph
                className={classNames(
                  'text-neutral-20',
                  'transition-colors duration-300 ease-out-cubic',
                  {
                    'group-hover:text-white': link
                  }
                )}
              >
                {paragraph}
              </Typography>
            ) : null}

            {link ? (
              <a
                href={link?.url}
                target={link?.target}
                className="before:absolute before:left-0 before:top-0 before:h-full before:w-full"
              />
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}

export default Pillar
